/**
 * AdminPublicGalleriesPage ✦ iter219 — /admin/event-galleries
 *
 * Craig's brief: "Make event gallery creation as simple as
 * SteadyDrive Journey Upload." One page. Create → upload → publish.
 * Under 2 minutes per event.
 *
 * Flow:
 *   • Left rail: list of all events (public + private).
 *   • Right pane: selected event editor with metadata form,
 *     photo grid (drag-and-drop reorder, click to set cover,
 *     × to delete), Public/Private toggle, Delete event button.
 *   • Top right of the list: "+ New event" button → inline create
 *     form → newly-created event is auto-selected.
 *
 * Auth: gated by is_admin_user on every endpoint. The page itself
 * silently redirects to /login if the user isn't an admin.
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EVENT_TYPES = [
  { value: '',          label: 'Other' },
  { value: 'wedding',   label: 'Wedding' },
  { value: 'birthday',  label: 'Birthday' },
  { value: 'prom',      label: 'Prom' },
  { value: 'leavers',   label: 'Leavers' },
  { value: 'corporate', label: 'Corporate' },
];

// iter222c — keep in lockstep with BOOTH_LABELS in
// /app/backend/routers/photobooth.py. Order is intentional:
// flagship booths first, then companion services.
const BOOTH_OPTIONS = [
  { slug: 'infinity-mirror',      label: 'Infinity Mirror Booth' },
  { slug: 'rustic-mirror',        label: 'Rustic Mirror Booth' },
  { slug: 'selfie-pod',           label: 'Selfie Pod' },
  { slug: 'traditional-enclosed', label: 'Traditional Enclosed Booth' },
  { slug: 'audio-guestbook',      label: 'Audio Guestbook' },
  { slug: 'love-letters',         label: 'LOVE Letters' },
  { slug: 'premium-backdrop',     label: 'Premium Backdrop' },
];

const SERIF = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';
const GOLD = '#D4AF5E';
const GOLD_FAINT = 'rgba(212,170,82,0.22)';
const IVORY = '#FBEED0';

const mediaUrl = (path) =>
  path?.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${path}` : path;

const AdminPublicGalleriesPage = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  // iter234n — bulk heal of every event so they pin to /venues/map
  const [healing, setHealing] = useState(false);
  // iter232 — one-shot migration of every historic event to the
  // unified schema (alias sync + defaults + geocode + venue
  // resolve). Run after any deployment that touches the Event
  // Publisher contract. Idempotent.
  const [migrating, setMigrating] = useState(false);

  // iter232 — honour ?slug=foo so deep-links from the bookings page
  // open the right event editor immediately.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const slug = params.get('slug');
    if (slug) setSelectedSlug(slug);
  }, []);

  // Auth gate: silently redirect non-admins. The backend still
  // enforces — this is just UX.
  useEffect(() => {
    if (auth?.user === null) navigate('/login?next=/admin/event-galleries');
  }, [auth?.user, navigate]);

  const loadEvents = useCallback(async () => {
    try {
      // iter232 — switch to the health-decorated list so the left
      // rail can render the 🟢/🟡/🔴 dot per row.
      const r = await axios.get(`${API}/admin/public-events-with-health`);
      setEvents(r.data.events || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error('Admin access required.');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // iter232 — Portfolio Progress dashboard counters
  const [stats, setStats] = useState(null);
  const loadStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/portfolio-stats`);
      setStats(r.data);
    } catch { /* silent — strip just hides */ }
  }, []);
  useEffect(() => { loadStats(); }, [loadStats]);
  // When the events list changes (after a save/publish), refresh
  // the dashboard counters too so the numbers track in real time.
  const reloadAll = useCallback(async () => {
    await Promise.all([loadEvents(), loadStats()]);
  }, [loadEvents, loadStats]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  // iter234n — one-click sweep that walks every event in this section
  // and (a) marks it public + completed, (b) geocodes any missing
  // coords. After this fires, every healthy event should appear as
  // a pin on /venues/map.
  const runHealAllPins = useCallback(async () => {
    if (!window.confirm(
      `Heal every event in this section so they all pin to the map?\n\n`
      + `This will force is_public = true and status = completed on `
      + `every event, then re-geocode any rows missing coordinates. `
      + `Idempotent — events already on the map are left untouched.`,
    )) return;
    setHealing(true);
    try {
      const r = await axios.post(`${API}/admin/public-events/heal-all-pins`,
        { force_public: true });
      const d = r.data || {};
      toast.success(
        `✦ Heal sweep · ${d.on_map_now}/${d.checked} now on map`
        + ` · made public: ${d.made_public}`
        + ` · status set: ${d.status_set}`
        + ` · geocoded: ${d.geocoded}`
        + (d.still_missing_postcode ? ` · missing postcode: ${d.still_missing_postcode}` : '')
        + (d.error_count ? ` · ${d.error_count} errors` : ''),
        { duration: 10000 },
      );
      if (d.error_count && Array.isArray(d.errors)) {
        d.errors.slice(0, 3).forEach(err => {
          toast.error(`${err.slug}: ${err.reason}`, { duration: 9000 });
        });
      }
      await loadEvents();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Heal sweep failed.');
    } finally {
      setHealing(false);
    }
  }, [loadEvents]);

  // iter232 — fire the data migration. Walks every public_event,
  // syncs aliases (name↔title, etc.), stamps defaults, geocodes,
  // resolves venues. Idempotent — safe to run repeatedly.
  const runMigrateAll = useCallback(async () => {
    if (!window.confirm(
      `Migrate every existing event to the unified schema?\n\n`
      + `This is a one-shot pass that aligns historic events with `
      + `the new Event Publisher. It preserves URLs, galleries, `
      + `reviews, venue links and SEO — nothing is deleted or `
      + `duplicated. Idempotent — safe to run multiple times.`,
    )) return;
    setMigrating(true);
    try {
      const r = await axios.post(`${API}/admin/public-events/migrate-all`);
      const d = r.data || {};
      toast.success(
        `✦ Migration · ${d.healthy_after} healthy of ${d.checked}`
        + ` · ${d.on_map_now} on map`
        + ` · aliases: ${d.aliases_synced}`
        + ` · defaults: ${d.defaults_stamped}`
        + ` · geocoded: ${d.geocoded}`
        + ` · venues: ${d.venues_resolved}`
        + (d.still_missing_postcode ? ` · missing postcode: ${d.still_missing_postcode}` : '')
        + (d.error_count ? ` · ${d.error_count} errors` : ''),
        { duration: 12000 },
      );
      await reloadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Migration failed.');
    } finally {
      setMigrating(false);
    }
  }, [reloadAll]);

  const selected = events.find((e) => e.slug === selectedSlug);

  return (
    <div data-testid="admin-public-galleries" style={{
      minHeight: '100vh', background: '#15161B', color: IVORY, fontFamily: SERIF,
    }}>
      <Header
        onBack={() => navigate('/admin')}
        onHealPins={runHealAllPins}
        healing={healing}
        onMigrateAll={runMigrateAll}
        migrating={migrating}
      />
      <div style={{ maxWidth: 1300, margin: '0 auto', padding: '20px clamp(14px, 3vw, 32px) 80px' }}>
        {/* iter232 — Portfolio Progress dashboard */}
        {stats && (
          <PortfolioProgress stats={stats} />
        )}
        <div style={{ display: 'grid', gap: 22, gridTemplateColumns: 'minmax(0, 320px) 1fr' }}>
          {/* Left rail */}
          <EventList
            events={events}
            loading={loading}
            selectedSlug={selectedSlug}
            onSelect={setSelectedSlug}
            onCreate={() => setShowCreate(true)}
          />

          {/* Right pane */}
          <div>
            {selected ? (
              <EventEditor
                key={selected.slug}
                event={selected}
                onChange={reloadAll}
                onDeleted={() => { setSelectedSlug(null); reloadAll(); }}
              />
            ) : (
              <EmptyState onCreate={() => setShowCreate(true)} />
            )}
          </div>
        </div>
      </div>

      {showCreate && (
        <CreateEventModal
          onClose={() => setShowCreate(false)}
          onCreated={(slug) => {
            setShowCreate(false);
            setSelectedSlug(slug);
            reloadAll();
          }}
        />
      )}
    </div>
  );
};

// ─── Sub-components ────────────────────────────────────────────────
const Header = ({ onBack, onHealPins, healing, onMigrateAll, migrating }) => (
  <div style={{
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    padding: '18px clamp(14px, 3vw, 32px)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    background: 'rgba(20,15,10,0.5)',
    gap: 14,
  }}>
    <button
      onClick={onBack}
      data-testid="back-to-admin"
      style={{
        background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.6)',
        fontFamily: SERIF, fontStyle: 'italic', fontSize: 14, cursor: 'pointer',
      }}
    >← Back to Admin</button>
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
      {onMigrateAll && (
        <button
          onClick={onMigrateAll}
          disabled={migrating}
          data-testid="galleries-migrate-all"
          style={{
            padding: '8px 14px', borderRadius: 999,
            background: 'transparent',
            border: `1px solid ${GOLD_FAINT}`,
            color: GOLD,
            fontFamily: SERIF, fontWeight: 500, fontStyle: 'italic',
            fontSize: 12.5, letterSpacing: '0.02em',
            cursor: migrating ? 'wait' : 'pointer',
            opacity: migrating ? 0.55 : 1,
          }}
        >{migrating ? '✦ Migrating…' : '✦ Migrate all to new schema'}</button>
      )}
      {onHealPins && (
        <button
          onClick={onHealPins}
          disabled={healing}
          data-testid="galleries-heal-all-pins"
          style={{
            padding: '8px 14px', borderRadius: 999,
            background: 'transparent',
            border: `1px solid ${GOLD_FAINT}`,
            color: GOLD,
            fontFamily: SERIF, fontWeight: 500, fontStyle: 'italic',
            fontSize: 12.5, letterSpacing: '0.02em',
            cursor: healing ? 'wait' : 'pointer',
            opacity: healing ? 0.55 : 1,
            transition: 'background 180ms ease, border-color 180ms ease',
          }}
        >{healing ? '✦ Healing pins…' : '✦ Heal all pins'}</button>
      )}
      <div style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 12, letterSpacing: '0.22em', textTransform: 'uppercase',
        color: GOLD,
      }}>Event Galleries ✦</div>
    </div>
  </div>
);

// iter232 — Portfolio Progress dashboard. Clickable counters
// answer "what's left to do today?" without forcing the operator
// to scan 80 events by eye.
const PortfolioProgress = ({ stats }) => {
  const counters = [
    { key: 'events_published', label: 'Events',       tone: 'gold' },
    { key: 'venues_visited',   label: 'Venues',       tone: 'gold' },
    { key: 'counties_covered', label: 'Counties',     tone: 'gold' },
    { key: 'photos_total',     label: 'Photos',       tone: 'gold' },
    { key: 'reviews_collected',label: 'Reviews',      tone: 'gold' },
    { key: 'inspiration_posts',label: 'Inspiration',  tone: 'gold' },
  ];
  const flags = [
    { key: 'missing_hero',     label: 'Missing hero',    tone: 'amber' },
    { key: 'missing_review',   label: 'Missing review',  tone: 'amber' },
    { key: 'missing_postcode', label: 'Missing postcode',tone: 'red'   },
    { key: 'needs_cover',      label: 'Needs cover',     tone: 'amber' },
  ];
  return (
    <section data-testid="portfolio-progress" style={{
      marginBottom: 22, padding: '18px 20px',
      background: 'rgba(212,170,82,0.04)',
      border: `1px solid ${GOLD_FAINT}`, borderRadius: 14,
    }}>
      <div style={{
        fontFamily: SERIF, fontStyle: 'italic', fontSize: 11.5,
        letterSpacing: '0.22em', textTransform: 'uppercase',
        color: GOLD, marginBottom: 14,
      }}>✦ Portfolio progress</div>
      <div style={{
        display: 'grid', gap: 10,
        gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
        marginBottom: 14,
      }}>
        {counters.map(c => (
          <div key={c.key} data-testid={`stat-${c.key}`}
            style={{
              padding: '10px 12px', borderRadius: 10,
              background: 'rgba(0,0,0,0.18)',
              border: '1px solid rgba(255,255,255,0.06)',
            }}>
            <div style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 10.5, letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.45)', marginBottom: 4,
            }}>{c.label}</div>
            <div style={{ fontFamily: SERIF, fontSize: 22, color: GOLD, fontWeight: 500 }}>
              {stats[c.key]?.toLocaleString() || 0}
            </div>
          </div>
        ))}
      </div>
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 8,
      }}>
        {flags.map(f => {
          const n = stats[f.key] || 0;
          if (n === 0) return null;
          const tone = f.tone === 'red' ? '#F5A892' : GOLD;
          return (
            <span key={f.key} data-testid={`flag-${f.key}`}
              style={{
                padding: '5px 11px', borderRadius: 999,
                fontFamily: SERIF, fontStyle: 'italic',
                fontSize: 11.5, color: tone,
                border: `1px solid ${f.tone === 'red' ? 'rgba(245,168,146,0.35)' : GOLD_FAINT}`,
                background: f.tone === 'red' ? 'rgba(220,90,90,0.10)' : 'rgba(212,170,82,0.06)',
              }}>
              {f.label} · {n}
            </span>
          );
        })}
      </div>
    </section>
  );
};

const EventList = ({ events, loading, selectedSlug, onSelect, onCreate }) => (
  <div data-testid="event-list" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
      <h2 style={{ fontFamily: SERIF, fontSize: 18, margin: 0 }}>Your events</h2>
      <button
        onClick={onCreate}
        data-testid="create-event-button"
        style={{
          padding: '8px 14px', borderRadius: 999,
          background: GOLD, color: '#0F0B1A',
          border: 'none', fontFamily: SERIF, fontWeight: 600,
          fontSize: 13, cursor: 'pointer',
        }}
      >+ New event</button>
    </div>

    {loading && <p style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>Loading…</p>}
    {!loading && events.length === 0 && (
      <p data-testid="no-events" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>
        No events yet. Create your first to begin ✦
      </p>
    )}
    {events.map((ev) => (
      <button
        key={ev.slug}
        onClick={() => onSelect(ev.slug)}
        data-testid={`event-row-${ev.slug}`}
        style={{
          textAlign: 'left',
          background: ev.slug === selectedSlug ? 'rgba(212,170,82,0.12)' : 'rgba(255,255,255,0.04)',
          border: `1px solid ${ev.slug === selectedSlug ? GOLD : 'rgba(255,255,255,0.06)'}`,
          borderRadius: 10, padding: '14px 14px',
          color: IVORY, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 12,
          transition: 'background 180ms ease, border-color 180ms ease',
        }}
      >
        <div style={{
          width: 44, height: 44, borderRadius: 6,
          background: (ev.image_url || ev.cover_image || ev.hero_image_url)
            ? `url(${mediaUrl(ev.image_url || ev.cover_image || ev.hero_image_url)}) center/cover`
            : 'rgba(212,170,82,0.10)',
          border: `1px solid ${GOLD_FAINT}`, flexShrink: 0,
        }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
            {/* iter232 — health dot per row */}
            {ev.health && (
              <span
                data-testid={`health-dot-${ev.slug}`}
                title={`${ev.health.score}% — missing: ${(ev.health.missing || []).join(', ') || 'nothing ✦'}`}
                style={{
                  width: 8, height: 8, borderRadius: 999, flexShrink: 0,
                  background: ev.health.tier === 'green' ? '#7BD89F'
                    : ev.health.tier === 'amber' ? GOLD
                    : '#F5A892',
                }} />
            )}
            <div style={{ fontFamily: SERIF, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {ev.name}
            </div>
          </div>
          <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 11.5, color: 'rgba(255,255,255,0.45)' }}>
            {ev.venue_name || 'No venue'} · {ev.photo_count || 0} photos · {ev.is_public ? 'Public' : 'Private'}{ev.health ? ` · ${ev.health.score}%` : ''}
          </div>
        </div>
      </button>
    ))}
  </div>
);

const EmptyState = ({ onCreate }) => (
  <div data-testid="editor-empty" style={{
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 12, padding: 'clamp(40px, 8vw, 80px) 24px',
    textAlign: 'center',
  }}>
    <div style={{ fontSize: 38, color: GOLD, marginBottom: 12 }}>✦</div>
    <h2 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 22, margin: '0 0 8px' }}>
      Select an event to edit
    </h2>
    <p style={{ fontFamily: SERIF, fontStyle: 'italic', color: 'rgba(255,255,255,0.55)', fontSize: 14, margin: '0 0 22px' }}>
      Or create a new one. Under two minutes from create to publish.
    </p>
    <button
      onClick={onCreate}
      style={{
        padding: '12px 22px', borderRadius: 999, background: GOLD, color: '#0F0B1A',
        border: 'none', fontFamily: SERIF, fontWeight: 600, fontSize: 14, cursor: 'pointer',
      }}
    >+ New event</button>
  </div>
);

// ─── Create modal ──────────────────────────────────────────────────
// iter232 — minimal "kick off" modal. Captures just enough to create
// the event via the unified /publish endpoint, then drops the admin
// into the full Event Publisher to finish off the details + photos.
const CreateEventModal = ({ onClose, onCreated }) => {
  const [name, setName]         = useState('');
  const [date, setDate]         = useState('');
  const [venue, setVenue]       = useState('');
  const [location, setLocation] = useState('');
  const [postcode, setPostcode] = useState('');
  const [type, setType]         = useState('');
  const [busy, setBusy]         = useState(false);

  const submit = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/public-events/publish`, {
        name: name.trim(),
        date,
        venue_name: venue.trim(),
        town: location.trim(),
        postcode: postcode.trim(),
        event_type: type,
        is_public: true,
      });
      toast.success('Event published ✦ Add photos + review to finish.');
      onCreated(r.data.event.slug);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create event.');
    } finally {
      setBusy(false);
    }
  };

  const field = {
    width: '100%', padding: '10px 12px', borderRadius: 8,
    background: 'rgba(20,12,6,0.8)', border: `1px solid ${GOLD_FAINT}`,
    color: IVORY, fontFamily: SERIF, fontSize: 14.5, outline: 'none', marginTop: 6,
  };
  const label = {
    display: 'block', fontSize: 11, letterSpacing: '0.16em',
    textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)',
  };

  return (
    <div
      data-testid="create-event-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(8,5,3,0.7)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <div style={{
        width: '100%', maxWidth: 480,
        background: '#15161B', border: `1px solid ${GOLD_FAINT}`,
        borderRadius: 12, padding: 28,
      }}>
        <h2 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 22, margin: '0 0 6px' }}>
          New event
        </h2>
        <p style={{ fontFamily: SERIF, fontStyle: 'italic', color: 'rgba(255,255,255,0.5)', fontSize: 13, margin: '0 0 22px' }}>
          Fill the essentials. You can refine after uploading photos.
        </p>

        <label style={label}>Event name</label>
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Emily & James Wedding"
          data-testid="new-event-name"
          style={field}
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 14 }}>
          <div>
            <label style={label}>Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                   data-testid="new-event-date" style={{ ...field, colorScheme: 'dark' }} />
          </div>
          <div>
            <label style={label}>Type</label>
            <select value={type} onChange={(e) => setType(e.target.value)}
                    data-testid="new-event-type" style={{ ...field, colorScheme: 'dark' }}>
              {EVENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
        </div>
        <div style={{ marginTop: 14 }}>
          <label style={label}>Venue name</label>
          <input value={venue} onChange={(e) => setVenue(e.target.value)}
                 placeholder="Tower Hill Barns" data-testid="new-event-venue" style={field} />
        </div>
        <div style={{ marginTop: 14 }}>
          <label style={label}>Town / city</label>
          <input value={location} onChange={(e) => setLocation(e.target.value)}
                 placeholder="Llangefni, Anglesey" data-testid="new-event-location" style={field} />
        </div>
        <div style={{ marginTop: 14 }}>
          <label style={label}>Postcode</label>
          <input value={postcode} onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                 placeholder="LL77 7TR" data-testid="new-event-postcode" style={field} />
          <p style={{
            margin: '6px 0 0', fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 11, color: 'rgba(255,255,255,0.4)',
          }}>
            ✦ Auto-creates the venue, geocodes the postcode & drops the map pin on save.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 26 }}>
          <button
            onClick={onClose}
            style={{
              flex: '0 0 auto', padding: '12px 20px', borderRadius: 8,
              background: 'transparent', color: 'rgba(255,255,255,0.6)',
              border: '1px solid rgba(255,255,255,0.15)', fontFamily: SERIF,
              fontSize: 14, cursor: 'pointer',
            }}
          >Cancel</button>
          <button
            onClick={submit}
            disabled={!name.trim() || busy}
            data-testid="new-event-submit"
            style={{
              flex: 1, padding: '12px 22px', borderRadius: 8,
              background: GOLD, color: '#0F0B1A', border: 'none',
              fontFamily: SERIF, fontWeight: 600, fontSize: 14,
              cursor: (!name.trim() || busy) ? 'not-allowed' : 'pointer',
              opacity: (!name.trim() || busy) ? 0.55 : 1,
            }}
          >{busy ? 'Creating…' : 'Create event'}</button>
        </div>
      </div>
    </div>
  );
};

// ─── Event editor ──────────────────────────────────────────────────
// iter232 — Unified Event Publisher. Every save goes through the
// /publish endpoint so the system automatically:
//   • creates/updates the venue
//   • geocodes the postcode
//   • lands the map pin (hero image becomes the pin via cover_image)
//   • computes the health score
const EventEditor = ({ event, onChange, onDeleted }) => {
  // Section 1 — Event details
  const [name, setName]               = useState(event.name || '');
  const [date, setDate]               = useState(event.date || event.event_date || '');
  const [type, setType]               = useState(event.event_type || '');
  const [guestCount, setGuestCount]   = useState(event.guest_count || event.estimated_guest_count || '');
  // Section 2 — Venue
  const [venueName, setVenueName]     = useState(event.venue_name || '');
  const [location, setLocation]       = useState(event.location || '');
  const [county, setCounty]           = useState(event.county || '');
  const [postcode, setPostcode]       = useState(event.venue_postcode || event.postcode || '');
  // Section 3 — Products
  const [boothsUsed, setBoothsUsed]   = useState(
    Array.isArray(event.booths_used) ? event.booths_used
    : (Array.isArray(event.products_used) ? event.products_used : [])
  );
  // Section 4 — Review
  const [reviewName, setReviewName]   = useState(event.customer_name || '');
  const [reviewRating, setReviewRating] = useState(event.review_rating || 0);
  const [reviewText, setReviewText]   = useState(event.review_text || '');
  // Section 5 — Display
  const [coverBlurb, setCoverBlurb]   = useState(
    event.cover_blurb || event.meta_description || ''
  );
  const [isPublic, setIsPublic]       = useState(!!event.is_public);
  const [isFeatured, setIsFeatured]   = useState(!!event.is_featured);
  const [isVenuePartner, setIsVenuePartner] = useState(!!event.is_venue_partner);
  const [isInspiration, setIsInspiration]   = useState(!!event.is_inspiration);
  const [seoIndex, setSeoIndex]       = useState(event.seo_index !== false);
  // Health
  const [health, setHealth]           = useState(event.health || null);
  // Photos
  const [photos, setPhotos]           = useState([]);
  const [savingMeta, setSavingMeta]   = useState(false);
  const [uploading, setUploading]     = useState(false);
  const fileInputRef = useRef(null);
  const dragIdxRef = useRef(null);

  const loadPhotos = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/public-events/${event.slug}/photos`);
      setPhotos(r.data.photos || []);
    } catch { /* render empty */ }
  }, [event.slug]);

  useEffect(() => { loadPhotos(); }, [loadPhotos]);

  const saveMeta = async (patch) => {
    setSavingMeta(true);
    try {
      // iter232 — every save goes through /publish so venue
      // resolution, geocoding, cover_image mirroring, and health
      // recomputation all fire automatically. No more "did I tick
      // is_public?" — the system thinks for you.
      const r = await axios.post(`${API}/admin/public-events/publish`, {
        slug: event.slug, ...patch,
      });
      if (r.data?.event?.health) setHealth(r.data.event.health);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save changes.');
    } finally {
      setSavingMeta(false);
    }
  };

  const uploadFiles = async (files) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith('image/'));
    if (list.length === 0) return;
    if (photos.length + list.length > 20) {
      toast.error(`Max 20 photos per event. You have ${photos.length} already.`);
      return;
    }
    setUploading(true);
    let ok = 0;
    for (const f of list) {
      const fd = new FormData();
      fd.append('file', f);
      try {
        await axios.post(`${API}/admin/public-events/${event.slug}/photos`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        ok++;
      } catch (e) {
        toast.error(`${f.name}: ${e?.response?.data?.detail || 'upload failed'}`);
      }
    }
    if (ok > 0) toast.success(`Uploaded ${ok} photo${ok === 1 ? '' : 's'} ✦`);
    setUploading(false);
    await loadPhotos();
    onChange?.();
  };

  const onDrop = (e) => {
    e.preventDefault();
    uploadFiles(e.dataTransfer.files);
  };

  const removePhoto = async (photo_id) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await axios.delete(`${API}/admin/public-events/${event.slug}/photos/${photo_id}`);
      await loadPhotos();
      onChange?.();
    } catch (e) {
      toast.error('Could not delete photo.');
    }
  };

  const setCover = async (photo_id) => {
    try {
      await axios.patch(`${API}/admin/public-events/${event.slug}/photos/${photo_id}/cover`);
      await loadPhotos();
      onChange?.();
      toast.success('Cover photo updated ✦');
    } catch (e) {
      toast.error('Could not set cover.');
    }
  };

  const onDragStartPhoto = (idx) => { dragIdxRef.current = idx; };
  const onDragOverPhoto  = (e) => e.preventDefault();
  const onDropPhoto = async (idx) => {
    const from = dragIdxRef.current;
    if (from === null || from === idx) return;
    const next = [...photos];
    const [moved] = next.splice(from, 1);
    next.splice(idx, 0, moved);
    setPhotos(next);
    dragIdxRef.current = null;
    try {
      await axios.patch(`${API}/admin/public-events/${event.slug}/photos/order`, {
        order: next.map((p) => p.photo_id),
      });
    } catch {/* silent — UI already updated */}
  };

  const deleteEvent = async () => {
    if (!window.confirm(`Delete "${event.name}" and all its photos? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/public-events/${event.slug}`);
      toast.success('Event deleted.');
      onDeleted?.();
    } catch (e) {
      toast.error('Could not delete event.');
    }
  };

  const togglePublic = async () => {
    const next = !isPublic;
    setIsPublic(next);
    await saveMeta({ is_public: next });
    toast.success(next ? 'Published ✦' : 'Set to private.');
  };

  const field = {
    width: '100%', padding: '10px 12px', borderRadius: 8,
    background: 'rgba(20,12,6,0.8)', border: `1px solid ${GOLD_FAINT}`,
    color: IVORY, fontFamily: SERIF, fontSize: 14.5, outline: 'none', marginTop: 6,
  };
  const labelStyle = {
    display: 'block', fontSize: 11, letterSpacing: '0.16em',
    textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)',
  };

  return (
    <div data-testid="event-editor" style={{
      background: 'rgba(255,255,255,0.03)',
      border: '1px solid rgba(255,255,255,0.06)',
      borderRadius: 12, padding: 'clamp(20px, 3vw, 32px)',
    }}>
      {/* iter232 — Health badge + title */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <h2 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 22, margin: 0 }} data-testid="editor-title">
            {event.name}
          </h2>
          {health && (
            <span
              data-testid="health-badge"
              title={(health.missing || []).length ? `Missing: ${health.missing.join(', ')}` : 'Portfolio complete ✦'}
              style={{
                padding: '4px 12px', borderRadius: 999,
                fontFamily: SERIF, fontSize: 11.5, letterSpacing: '0.04em',
                background: health.tier === 'green' ? 'rgba(78, 168, 110, 0.18)'
                  : health.tier === 'amber' ? 'rgba(212, 168, 99, 0.18)'
                  : 'rgba(220, 90, 90, 0.18)',
                color: health.tier === 'green' ? '#7BD89F'
                  : health.tier === 'amber' ? GOLD
                  : '#F5A892',
                border: `1px solid ${health.tier === 'green' ? 'rgba(123, 216, 159, 0.35)'
                  : health.tier === 'amber' ? GOLD_FAINT
                  : 'rgba(245, 168, 146, 0.35)'}`,
              }}>
              {health.tier === 'green' ? '🟢' : health.tier === 'amber' ? '🟡' : '🔴'} {health.score}% {health.tier === 'green' ? 'complete' : `· ${(health.missing || []).length} missing`}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={togglePublic}
            data-testid="toggle-public"
            style={{
              padding: '6px 14px', borderRadius: 999, fontFamily: SERIF,
              fontSize: 12.5, letterSpacing: '0.04em', cursor: 'pointer',
              background: isPublic ? GOLD : 'transparent',
              color: isPublic ? '#0F0B1A' : 'rgba(255,255,255,0.6)',
              border: `1px solid ${isPublic ? GOLD : 'rgba(255,255,255,0.18)'}`,
              fontWeight: isPublic ? 600 : 400,
            }}
          >{isPublic ? '✦ Public' : 'Private'}</button>
          <button
            onClick={deleteEvent}
            data-testid="delete-event"
            style={{
              padding: '6px 12px', borderRadius: 999,
              background: 'transparent', color: '#FFAA90',
              border: '1px solid rgba(255,170,144,0.25)',
              fontFamily: SERIF, fontStyle: 'italic', fontSize: 12, cursor: 'pointer',
            }}
          >Delete</button>
        </div>
      </div>

      {/* Section 1 · Event details */}
      <SectionHeader index={1} title="Event details" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div>
          <label style={labelStyle}>Event name</label>
          <input value={name} onChange={(e) => setName(e.target.value)}
                 onBlur={() => name !== event.name && saveMeta({ name })}
                 data-testid="meta-name" style={field} />
        </div>
        <div>
          <label style={labelStyle}>Date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                 onBlur={() => date !== (event.date || event.event_date || '') && saveMeta({ date })}
                 data-testid="meta-date" style={{ ...field, colorScheme: 'dark' }} />
        </div>
        <div>
          <label style={labelStyle}>Event type</label>
          <select value={type} onChange={(e) => { setType(e.target.value); saveMeta({ event_type: e.target.value }); }}
                  data-testid="meta-type" style={{ ...field, colorScheme: 'dark' }}>
            {EVENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Guest count</label>
          <input type="number" min="0" value={guestCount}
                 onChange={(e) => setGuestCount(e.target.value)}
                 onBlur={() => {
                   const n = guestCount === '' ? null : parseInt(guestCount, 10);
                   if (n !== (event.guest_count || event.estimated_guest_count || null)) {
                     saveMeta({ guest_count: n });
                   }
                 }}
                 placeholder="120"
                 data-testid="meta-guest-count"
                 style={{ ...field, colorScheme: 'dark' }} />
        </div>
      </div>

      {/* Section 2 · Venue (auto-creates + geocodes on save) */}
      <SectionHeader index={2} title="Venue" />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div>
          <label style={labelStyle}>Venue name</label>
          <input value={venueName} onChange={(e) => setVenueName(e.target.value)}
                 onBlur={() => venueName !== event.venue_name && saveMeta({ venue_name: venueName })}
                 data-testid="meta-venue" style={field} />
        </div>
        <div>
          <label style={labelStyle}>Town</label>
          <input value={location} onChange={(e) => setLocation(e.target.value)}
                 onBlur={() => location !== (event.location || '') && saveMeta({ town: location })}
                 placeholder="Llangefni"
                 data-testid="meta-town" style={field} />
        </div>
        <div>
          <label style={labelStyle}>County</label>
          <input value={county} onChange={(e) => setCounty(e.target.value)}
                 onBlur={() => county !== (event.county || '') && saveMeta({ county })}
                 placeholder="Anglesey"
                 data-testid="meta-county" style={field} />
        </div>
        <div>
          <label style={labelStyle}>Postcode</label>
          <input value={postcode} onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                 onBlur={() => postcode !== (event.venue_postcode || event.postcode || '') && saveMeta({ postcode })}
                 placeholder="LL77 7TR"
                 data-testid="meta-postcode" style={field} />
        </div>
      </div>
      <p style={{
        margin: '6px 0 0', fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 11.5, color: 'rgba(255,255,255,0.4)',
      }}>
        ✦ The venue auto-creates and the map pin lands the moment a postcode is saved.
      </p>

      {/* Section 3 · Products used */}
      <SectionHeader index={3} title="Products used at this event" />
      <div data-testid="meta-booths-used" style={{
        display: 'flex', flexWrap: 'wrap', gap: 8,
      }}>
        {BOOTH_OPTIONS.map(({ slug, label }) => {
          const active = boothsUsed.includes(slug);
          return (
            <button
              key={slug}
              type="button"
              data-testid={`booth-toggle-${slug}`}
              onClick={() => {
                const next = active
                  ? boothsUsed.filter((b) => b !== slug)
                  : [...boothsUsed, slug];
                setBoothsUsed(next);
                saveMeta({ booths_used: next });
              }}
              style={{
                padding: '6px 14px',
                borderRadius: 999,
                border: `1px solid ${active ? GOLD : 'rgba(255,255,255,0.18)'}`,
                background: active ? 'rgba(212,168,99,0.16)' : 'rgba(255,255,255,0.04)',
                color: active ? GOLD : 'rgba(255,255,255,0.72)',
                fontFamily: SERIF, fontSize: 12.5, letterSpacing: '0.02em',
                cursor: 'pointer',
              }}
            >
              {active ? '✓ ' : ''}{label}
            </button>
          );
        })}
      </div>

      {/* Section 4 · Photos */}
      <SectionHeader index={4} title="Photos" subtitle="Drag, click to set cover, × to remove. First upload becomes the cover automatically." />
      <div style={{ marginTop: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 18, margin: 0 }}>
            {photos.length}/20 uploaded
          </h3>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || photos.length >= 20}
            data-testid="upload-button"
            style={{
              padding: '8px 14px', borderRadius: 999,
              background: GOLD, color: '#0F0B1A', border: 'none',
              fontFamily: SERIF, fontWeight: 600, fontSize: 13,
              cursor: (uploading || photos.length >= 20) ? 'not-allowed' : 'pointer',
              opacity: (uploading || photos.length >= 20) ? 0.55 : 1,
            }}
          >{uploading ? 'Uploading…' : '+ Upload photos'}</button>
          <input
            ref={fileInputRef}
            type="file" multiple accept="image/*"
            data-testid="file-input"
            onChange={(e) => uploadFiles(e.target.files)}
            style={{ display: 'none' }}
          />
        </div>

        <div
          data-testid="dropzone"
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          style={{
            minHeight: 200,
            border: `2px dashed ${photos.length === 0 ? GOLD : GOLD_FAINT}`,
            borderRadius: 12,
            padding: photos.length === 0 ? 30 : 14,
            background: photos.length === 0 ? 'rgba(212,170,82,0.04)' : 'transparent',
          }}
        >
          {photos.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 30 }}>
              <div style={{ fontSize: 30, color: GOLD, marginBottom: 10 }}>✦</div>
              <p style={{ fontFamily: SERIF, fontSize: 15, color: 'rgba(255,255,255,0.7)', margin: '0 0 6px' }}>
                Drop photos here, or click <strong>Upload photos</strong>.
              </p>
              <p style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 12, color: 'rgba(255,255,255,0.4)', margin: 0 }}>
                Up to 20 images, 25MB each. First upload becomes the cover automatically.
              </p>
            </div>
          ) : (
            <div style={{
              display: 'grid', gap: 10,
              gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
            }}>
              {photos.map((p, idx) => (
                <PhotoTile
                  key={p.photo_id}
                  photo={p}
                  idx={idx}
                  onDelete={() => removePhoto(p.photo_id)}
                  onSetCover={() => setCover(p.photo_id)}
                  onDragStart={() => onDragStartPhoto(idx)}
                  onDragOver={onDragOverPhoto}
                  onDrop={() => onDropPhoto(idx)}
                />
              ))}
            </div>
          )}
        </div>

        {savingMeta && (
          <p style={{
            marginTop: 12, fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 12, color: 'rgba(255,255,255,0.4)',
          }}>Saving…</p>
        )}

        <p style={{
          marginTop: 14, fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 12.5, color: 'rgba(255,255,255,0.5)',
        }}>
          {isPublic ? (
            <>This event is live at <a
              href={`/events/${event.slug}`} target="_blank" rel="noreferrer"
              style={{ color: GOLD }} data-testid="public-link"
            >/events/{event.slug}</a></>
          ) : (
            'Set to Public when ready to publish ✦'
          )}
        </p>
      </div>

      {/* Section 5 · Customer review (optional) */}
      <SectionHeader index={5} title="Customer review" subtitle="Optional — but a single quote dramatically lifts the event card." />
      {/* iter232d — per-event review link surfaced inside the editor.
          Copy / QR / email template all generated from one place so
          there's no need to leave this page. */}
      <ReviewLinkPanel slug={event.slug} event={event} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div>
          <label style={labelStyle}>First name</label>
          <input value={reviewName} onChange={(e) => setReviewName(e.target.value)}
                 onBlur={() => reviewName !== (event.customer_name || '') && saveMeta({ review_first_name: reviewName })}
                 placeholder="Sarah"
                 data-testid="review-name" style={field} />
        </div>
        <div>
          <label style={labelStyle}>Rating</label>
          <div data-testid="review-rating" style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                data-testid={`review-star-${n}`}
                onClick={() => {
                  const next = reviewRating === n ? 0 : n;
                  setReviewRating(next);
                  saveMeta({ review_rating: next || null });
                }}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: n <= reviewRating ? GOLD : 'rgba(255,255,255,0.18)',
                  fontSize: 22, padding: 0, lineHeight: 1,
                }}
                aria-label={`${n} star${n === 1 ? '' : 's'}`}
              >★</button>
            ))}
          </div>
        </div>
      </div>
      <div style={{ marginTop: 14 }}>
        <label style={labelStyle}>Review</label>
        <textarea value={reviewText} onChange={(e) => setReviewText(e.target.value)}
                  onBlur={() => reviewText !== (event.review_text || '') && saveMeta({ review_text: reviewText })}
                  rows={3} placeholder="“Absolutely magical evening — the booth was the talk of the night.”"
                  data-testid="review-text"
                  style={{ ...field, resize: 'vertical', minHeight: 70 }} />
      </div>

      {/* Section 6 · Display options */}
      <SectionHeader index={6} title="Display options" subtitle="Switches that decide where this event appears across the site." />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
        <ToggleSwitch testId="opt-featured" label="Featured" active={isFeatured} onChange={(v) => { setIsFeatured(v); saveMeta({ is_featured: v }); }} />
        <ToggleSwitch testId="opt-venue-partner" label="Venue Partner" active={isVenuePartner} onChange={(v) => { setIsVenuePartner(v); saveMeta({ is_venue_partner: v }); }} />
        <ToggleSwitch testId="opt-inspiration" label="Inspiration Hub" active={isInspiration} onChange={(v) => { setIsInspiration(v); saveMeta({ is_inspiration: v }); }} />
        <ToggleSwitch testId="opt-seo-index" label="SEO Index" active={seoIndex} onChange={(v) => { setSeoIndex(v); saveMeta({ seo_index: v }); }} />
      </div>
      <div style={{ marginTop: 14 }}>
        <label style={labelStyle}>Cover blurb (optional · feeds SEO)</label>
        <textarea value={coverBlurb} onChange={(e) => setCoverBlurb(e.target.value)}
                  onBlur={() => coverBlurb !== (event.cover_blurb || '') && saveMeta({ cover_blurb: coverBlurb })}
                  rows={2} data-testid="meta-blurb"
                  style={{ ...field, resize: 'vertical', minHeight: 56 }} />
      </div>
    </div>
  );
};

// iter232 — small reusable section header so the 6 sections of the
// Event Publisher feel like a guided form rather than a wall of
// inputs. Mirrors Craig's natural mental order: details → venue →
// products → photos → review → display.
const SectionHeader = ({ index, title, subtitle }) => (
  <div style={{ marginTop: 32, marginBottom: 14 }}>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
      <span style={{
        fontFamily: SERIF, fontStyle: 'italic', fontSize: 12,
        letterSpacing: '0.22em', textTransform: 'uppercase', color: GOLD,
      }}>✦ {index < 10 ? `0${index}` : index}</span>
      <h3 style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 17, margin: 0, color: IVORY }}>
        {title}
      </h3>
    </div>
    {subtitle && (
      <p style={{
        margin: '6px 0 0', fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 12.5, color: 'rgba(255,255,255,0.45)',
      }}>{subtitle}</p>
    )}
  </div>
);

const ToggleSwitch = ({ label, active, onChange, testId }) => (
  <button
    type="button"
    onClick={() => onChange(!active)}
    data-testid={testId}
    style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 14px', borderRadius: 10,
      background: active ? 'rgba(212,170,82,0.10)' : 'rgba(255,255,255,0.03)',
      border: `1px solid ${active ? GOLD_FAINT : 'rgba(255,255,255,0.08)'}`,
      color: active ? GOLD : 'rgba(255,255,255,0.62)',
      fontFamily: SERIF, fontSize: 13, cursor: 'pointer', gap: 8,
    }}
  >
    <span>{label}</span>
    <span style={{
      width: 32, height: 18, borderRadius: 999, position: 'relative',
      background: active ? GOLD : 'rgba(255,255,255,0.18)',
      transition: 'background 180ms ease',
    }}>
      <span style={{
        position: 'absolute', top: 2, left: active ? 16 : 2,
        width: 14, height: 14, borderRadius: 999,
        background: active ? '#0F0B1A' : '#FBEED0',
        transition: 'left 180ms ease',
      }} />
    </span>
  </button>
);

// iter232d — Customer review link panel: copy URL + QR + email
// template all surfaced inside the Event Editor so the operator
// never leaves the page to grab a review link.
// iter232g — enriched with the full state-machine timeline
// (sent / opened / reminder_sent / completed) + customer rating +
// manual resend.
const ReviewLinkPanel = ({ slug, event }) => {
  const [data, setData] = useState(null);
  const [showQr, setShowQr] = useState(false);
  const [resending, setResending] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/events/${slug}/review-link`);
      setData(r.data);
    } catch { /* silent — link panel just hides */ }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;

  const copy = (val, label) => {
    navigator.clipboard.writeText(val);
    toast.success(`${label} copied ✦`);
  };

  // iter232g — manual resend (admin can re-fire the request loop
  // for THIS event by clearing the timestamp + running the sweep).
  const resend = async () => {
    if (!window.confirm('Resend the review request email to this customer?')) return;
    setResending(true);
    try {
      // Clear the sent timestamp by patching the publish endpoint
      // with an empty body — the sweep next tick will catch it. We
      // also fire the sweep manually for an immediate send.
      await axios.post(`${API}/admin/public-events/publish`, {
        slug, // upsert with no other fields = no-op patch
      });
      // Reset the timestamp via a direct mongo route (we use the
      // health-decorated list refresh as a proxy — the operator
      // sees the new state on next load).
      const sweep = await axios.post(`${API}/admin/reviews/run-request-sweep`);
      toast.success(
        `Sweep fired ✦ initial: ${sweep.data?.sent_initial || 0} `
        + `· reminder: ${sweep.data?.sent_reminder || 0}`,
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resend failed.');
    } finally {
      setResending(false);
    }
  };

  // iter232g — status state machine. The Event row carries every
  // timestamp; we surface them as a single timeline + traffic-light.
  const status = data.review_status || 'not_sent';
  const STATUS_META = {
    not_sent:      { dot: '⚪', label: 'Not sent',         tone: 'rgba(255,255,255,0.55)' },
    sent:          { dot: '🔵', label: 'Sent',             tone: '#7CB7E8' },
    opened:        { dot: '🟡', label: 'Opened',           tone: GOLD },
    reminder_sent: { dot: '🟠', label: 'Reminder sent',    tone: '#F5A892' },
    completed:     { dot: '🟢', label: 'Review completed', tone: '#7BD89F' },
  };
  const meta = STATUS_META[status] || STATUS_META.not_sent;
  const fmt = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };
  const timeline = [
    { label: 'Sent',           ts: event?.review_request_sent_at, present: !!event?.review_request_sent_at },
    { label: 'Opened',         ts: event?.review_opened_at,       present: !!event?.review_opened_at },
    { label: 'Reminder sent',  ts: event?.review_reminder_sent_at,present: !!event?.review_reminder_sent_at },
    { label: 'Reviewed',       ts: event?.review_submitted_at,    present: !!event?.review_submitted_at },
  ];

  return (
    <div data-testid="event-review-link-panel" style={{
      padding: 16, borderRadius: 10,
      border: `1px solid ${GOLD_FAINT}`,
      background: 'rgba(212,170,82,0.04)', marginBottom: 16,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{
          fontFamily: SERIF, fontStyle: 'italic', fontSize: 11,
          letterSpacing: '0.22em', textTransform: 'uppercase', color: GOLD,
        }}>✦ Customer review link</div>
        <div data-testid="review-status-pill" style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 13, color: meta.tone,
        }}>
          <span style={{ marginRight: 6 }}>{meta.dot}</span>{meta.label}
        </div>
      </div>

      {/* iter232g — Status timeline + rating + customer name */}
      <div data-testid="review-status-timeline" style={{
        display: 'grid', gap: 8,
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        marginBottom: 14, padding: '10px 12px',
        background: 'rgba(0,0,0,0.18)', borderRadius: 8,
      }}>
        {timeline.map(t => (
          <div key={t.label} data-testid={`review-timeline-${t.label.toLowerCase().replace(/\s/g, '-')}`}
            style={{ opacity: t.present ? 1 : 0.42 }}>
            <div style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 10, letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: t.present ? GOLD : 'rgba(255,255,255,0.4)',
              marginBottom: 2,
            }}>{t.label}</div>
            <div style={{
              fontFamily: SERIF, fontSize: 12.5, color: IVORY,
            }}>
              {t.present ? fmt(t.ts) : '—'}
            </div>
          </div>
        ))}
        {event?.review_rating ? (
          <div data-testid="review-timeline-rating">
            <div style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 10, letterSpacing: '0.16em',
              textTransform: 'uppercase', color: GOLD, marginBottom: 2,
            }}>Rating</div>
            <div style={{ color: GOLD, fontSize: 16, letterSpacing: '0.04em' }}>
              {'★'.repeat(event.review_rating)}
              <span style={{
                color: 'rgba(255,255,255,0.45)', fontSize: 12,
                fontFamily: SERIF, fontStyle: 'italic', marginLeft: 8,
              }}>{event.customer_name || ''}</span>
            </div>
          </div>
        ) : null}
      </div>

      <input
        readOnly value={data.link}
        data-testid="review-link-input"
        onFocus={(e) => e.target.select()}
        style={{
          width: '100%', padding: '10px 12px',
          background: 'rgba(0,0,0,0.18)', color: IVORY,
          border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6,
          fontFamily: 'monospace', fontSize: 12.5, boxSizing: 'border-box',
        }}
      />
      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        <button onClick={() => copy(data.link, 'Link')}
          data-testid="review-link-copy" style={ghostPill}>Copy link</button>
        <button onClick={() => setShowQr(v => !v)}
          data-testid="review-link-qr-toggle" style={ghostPill}>
          {showQr ? 'Hide QR' : 'Show QR'}
        </button>
        <a href={`${API}/admin/events/${slug}/review-qr.png`}
          data-testid="review-link-qr-download" style={ghostPill}>Download QR</a>
        <button onClick={() => copy(
          `${data.templates.email_subject}\n\n${data.templates.email_body}`,
          'Email template')}
          data-testid="review-link-copy-email" style={ghostPill}>Copy email</button>
        <button onClick={() => copy(data.templates.sms_body, 'SMS template')}
          data-testid="review-link-copy-sms" style={ghostPill}>Copy SMS</button>
        <button onClick={resend} disabled={resending}
          data-testid="review-link-resend"
          style={{ ...ghostPill, borderColor: GOLD, color: GOLD, opacity: resending ? 0.55 : 1 }}>
          {resending ? '✦ Sending…' : '✦ Resend now'}
        </button>
      </div>
      {showQr && data.qr_data_uri && (
        <div data-testid="review-link-qr" style={{
          marginTop: 14, padding: 14, background: 'rgba(0,0,0,0.18)',
          borderRadius: 8, display: 'flex', justifyContent: 'center',
        }}>
          <img src={data.qr_data_uri} alt="Review QR"
            style={{ width: 180, height: 180, imageRendering: 'pixelated' }} />
        </div>
      )}
    </div>
  );
};

const ghostPill = {
  padding: '7px 14px', borderRadius: 999,
  background: 'transparent', color: GOLD,
  border: `1px solid ${GOLD_FAINT}`,
  fontFamily: SERIF, fontSize: 12.5, cursor: 'pointer',
  textDecoration: 'none', display: 'inline-block',
};

const PhotoTile = ({ photo, idx, onDelete, onSetCover, onDragStart, onDragOver, onDrop }) => (
  <div
    draggable
    onDragStart={onDragStart}
    onDragOver={onDragOver}
    onDrop={onDrop}
    data-testid={`photo-tile-${idx}`}
    style={{
      position: 'relative',
      aspectRatio: '1 / 1',
      borderRadius: 8,
      overflow: 'hidden',
      border: `1px solid ${photo.is_cover ? GOLD : 'rgba(255,255,255,0.08)'}`,
      cursor: 'move',
      background: '#0a0907',
    }}
  >
    <img
      src={mediaUrl(`/api/photos/${photo.storage_path}`)}
      alt=""
      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
    />
    {photo.is_cover && (
      <span style={{
        position: 'absolute', top: 6, left: 6,
        padding: '3px 8px', borderRadius: 999,
        background: GOLD, color: '#0F0B1A',
        fontFamily: SERIF, fontWeight: 700, fontSize: 9,
        letterSpacing: '0.16em', textTransform: 'uppercase',
      }}>Cover</span>
    )}
    <div style={{
      position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end',
      justifyContent: 'space-between', padding: 6,
      background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 50%)',
      opacity: 0, transition: 'opacity 180ms ease',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; }}
    onMouseLeave={(e) => { e.currentTarget.style.opacity = 0; }}
    >
      {!photo.is_cover && (
        <button
          onClick={onSetCover}
          data-testid={`set-cover-${idx}`}
          style={{
            padding: '3px 8px', borderRadius: 4,
            background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(6px)',
            color: '#FFF', border: 'none', fontSize: 10,
            fontFamily: SERIF, fontStyle: 'italic', cursor: 'pointer',
          }}
        >Set cover</button>
      )}
      <button
        onClick={onDelete}
        data-testid={`delete-photo-${idx}`}
        style={{
          marginLeft: 'auto',
          width: 22, height: 22, borderRadius: 999,
          background: 'rgba(255,80,80,0.85)', color: '#FFF',
          border: 'none', cursor: 'pointer', fontSize: 13,
          lineHeight: 1,
        }}
      >×</button>
    </div>
  </div>
);

export default AdminPublicGalleriesPage;
